import React, { Component, createRef } from 'react';
import { FormattedMessage } from 'react-intl';
import AiMessageBubble from './AiMessageBubble';

/**
 * AiTranscript — scrollable message list with:
 * - Empty state (guided prompts)
 * - Transcript state (message bubbles)
 * - Pending state (typing indicator as next bubble)
 *
 * role="log" aria-live="polite" for screen-reader announcements.
 */
class AiTranscript extends Component {
    constructor(props) {
        super(props);
        this._endRef = createRef();
    }

    componentDidUpdate(prevProps) {
        // Auto-scroll to bottom when new messages arrive or pending state changes
        if (
            prevProps.transcript.length !== this.props.transcript.length ||
            prevProps.isPending !== this.props.isPending
        ) {
            this._scrollToBottom();
        }
    }

    _scrollToBottom = () => {
        if (this._endRef.current) {
            this._endRef.current.scrollIntoView({ behavior: 'smooth' });
        }
    };

    _renderEmptyState = () => {
        const { onExampleClick } = this.props;
        const exampleKeys = [
            'TemplateAssistant.Empty.Example1',
            'TemplateAssistant.Empty.Example2',
            'TemplateAssistant.Empty.Example3',
        ];

        return (
            <div
                style={{
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    justifyContent: 'center',
                    height: '100%',
                    padding: '24px 16px',
                    textAlign: 'center',
                }}
            >
                <div
                    style={{
                        width: 56,
                        height: 56,
                        borderRadius: '50%',
                        backgroundColor: '#6f42c1',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        marginBottom: 12,
                    }}
                >
                    <i className="fa-solid fa-robot" style={{ color: '#fff', fontSize: '1.5rem' }} aria-hidden="true" />
                </div>

                <p style={{ fontSize: '0.85rem', color: '#495057', marginBottom: 12 }}>
                    <FormattedMessage id="TemplateAssistant.Empty.Intro" />
                </p>

                <p style={{ fontSize: '0.78rem', color: '#6c757d', marginBottom: 6 }}>
                    <FormattedMessage id="TemplateAssistant.Empty.ExamplesHeading" />
                </p>

                <div style={{ display: 'flex', flexDirection: 'column', gap: 6, width: '100%' }}>
                    {exampleKeys.map(key => (
                        <FormattedMessage key={key} id={key}>
                            {text => (
                                <button
                                    type="button"
                                    onClick={() => onExampleClick(text)}
                                    style={{
                                        background: 'none',
                                        border: '1px solid #dee2e6',
                                        borderRadius: 8,
                                        padding: '6px 10px',
                                        fontSize: '0.8rem',
                                        color: '#0d6efd',
                                        cursor: 'pointer',
                                        textAlign: 'left',
                                        lineHeight: 1.4,
                                        backgroundColor: '#f8f9fa',
                                        transition: 'background-color 0.15s',
                                    }}
                                    onMouseEnter={e => { e.currentTarget.style.backgroundColor = '#e8f0fe'; }}
                                    onMouseLeave={e => { e.currentTarget.style.backgroundColor = '#f8f9fa'; }}
                                >
                                    {text}
                                </button>
                            )}
                        </FormattedMessage>
                    ))}
                </div>
            </div>
        );
    };

    _renderPendingBubble = () => (
        <div
            style={{
                display: 'flex',
                justifyContent: 'flex-start',
                marginBottom: 8,
            }}
        >
            <div style={{ display: 'flex', alignItems: 'flex-start', gap: 6 }}>
                {/* Robot icon */}
                <div
                    style={{
                        width: 28,
                        height: 28,
                        borderRadius: '50%',
                        backgroundColor: '#6f42c1',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        flexShrink: 0,
                        marginTop: 2,
                    }}
                >
                    <i className="fa-solid fa-robot" aria-hidden="true" style={{ color: '#fff', fontSize: '0.75rem' }} />
                </div>

                {/* Typing indicator */}
                <div
                    style={{
                        backgroundColor: '#fff',
                        border: '1px solid #dee2e6',
                        borderRadius: '4px 12px 12px 12px',
                        padding: '10px 14px',
                        display: 'flex',
                        alignItems: 'center',
                        gap: 4,
                    }}
                >
                    {[0, 1, 2].map(i => (
                        <span
                            key={i}
                            style={{
                                width: 7,
                                height: 7,
                                borderRadius: '50%',
                                backgroundColor: '#6c757d',
                                display: 'inline-block',
                                animation: 'aiTypingDot 1.2s ease-in-out infinite',
                                animationDelay: `${i * 0.2}s`,
                            }}
                        />
                    ))}
                </div>
            </div>
        </div>
    );

    render() {
        const { transcript, isPending, onUndo, intl } = this.props;
        const isEmpty = transcript.length === 0;

        const conversationLabel = intl
            ? intl.formatMessage({ id: 'TemplateAssistant.Transcript.AriaLabel' })
            : 'AI Assistant conversation';

        return (
            <div
                role="log"
                aria-live="polite"
                aria-label={conversationLabel}
                style={{
                    flexGrow: 1,
                    overflowY: 'auto',
                    padding: isEmpty ? 0 : '12px 12px 0',
                    minHeight: 0,
                }}
            >
                {isEmpty && !isPending
                    ? this._renderEmptyState()
                    : (
                        <div>
                            {transcript.map((entry, index) => (
                                <AiMessageBubble
                                    key={entry.id != null ? entry.id : index}
                                    entry={entry}
                                    onUndo={onUndo}
                                />
                            ))}
                            {isPending && this._renderPendingBubble()}
                            <div ref={this._endRef} />
                        </div>
                    )
                }
            </div>
        );
    }
}

export default AiTranscript;
